import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Calendar, Award, Clock, Video } from "lucide-react";
import { api, openExternal } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton, GoldButton } from "../components/site/Primitives";
import Testimonials from "../components/site/Testimonials";
import FaqAccordion from "../components/site/FaqAccordion";

const EASE = [0.16, 1, 0.3, 1];

const SCHEDULE = [
  { cohort: "Winter Cohort", dates: "Placeholder — dates TBA", days: "Live · Evenings" },
  { cohort: "Spring Cohort", dates: "Placeholder — dates TBA", days: "Live · Weekends" },
  { cohort: "Summer Cohort", dates: "Placeholder — dates TBA", days: "Live · Evenings" },
];

const QUICK = [
  { icon: Clock, label: "27 Hours" },
  { icon: Video, label: "Live Online" },
  { icon: Award, label: "NY State Approved" },
];

export default function Course() {
  const [course, setCourse] = useState(null);
  useEffect(() => {
    api.get("/programs?track=trainee").then((r) => setCourse(r.data[0])).catch(() => {});
  }, []);

  const register = () => openExternal(course?.checkout_url);

  return (
    <div data-testid="course-page">
      <PageHero
        overline="New York State Nail Trainee Education"
        title="4 Days to Stateboard Success: 27HR Nail Trainee Course"
        subtitle="Live online instruction that qualifies you to sit for the New York State Nail Specialty Licensing Exam — taught by a certified instructor with 20+ years of experience."
      >
        <PrimaryButton onClick={register} data-testid="course-register-btn">Register Now</PrimaryButton>
        <div className="flex flex-wrap items-center gap-5 pt-3">
          {QUICK.map((q) => (
            <span key={q.label} className="flex items-center gap-2 text-sm text-brand-ink/70"><q.icon size={16} className="text-brand-rose" />{q.label}</span>
          ))}
        </div>
      </PageHero>

      {/* Benefits */}
      <section className="bg-brand-cream py-24 md:py-32" data-testid="course-benefits">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <Overline className="mb-5">What you&apos;ll gain</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">Everything you need to get licensed.</h2>
            <p className="text-brand-ink/70 font-light mt-6 leading-relaxed">{course?.description}</p>
          </Reveal>
          <Reveal delay={0.1} className="grid gap-3">
            {(course?.features || []).map((f) => (
              <div key={f} className="flex items-center gap-4 bg-white rounded-2xl border border-brand-ink/10 px-6 py-5">
                <span className="h-9 w-9 rounded-full bg-brand-blush flex items-center justify-center text-brand-rose shrink-0"><Check size={17} /></span>
                <span className="text-brand-ink font-light">{f}</span>
              </div>
            ))}
            <div className="flex items-center justify-between bg-brand-ink text-brand-cream rounded-2xl px-7 py-6 mt-2">
              <div><p className="text-xs uppercase tracking-[0.25em] text-brand-gold">Tuition</p><p className="font-serif text-4xl mt-1">{course?.price}</p></div>
              <GoldButton onClick={register} data-testid="course-register-btn-2">Register</GoldButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Schedule */}
      <section className="bg-brand-blush py-24 md:py-32" data-testid="course-schedule">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="mb-12"><Overline className="mb-5">Class schedule</Overline><h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">Upcoming cohorts</h2></Reveal>
          <div className="grid gap-4 md:grid-cols-3">
            {SCHEDULE.map((s, i) => (
              <motion.div key={s.cohort} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }} className="bg-white rounded-3xl border border-brand-ink/10 p-8">
                <Calendar className="text-brand-rose" size={24} />
                <h3 className="font-serif text-2xl text-brand-ink mt-5">{s.cohort}</h3>
                <p className="text-brand-ink/60 font-light mt-2">{s.dates}</p>
                <p className="text-brand-ink/60 font-light text-sm mt-1">{s.days}</p>
                <button onClick={register} data-testid={`schedule-register-${i}`} className="mt-6 text-sm border-b border-brand-gold pb-0.5 text-brand-ink hover:text-brand-rose transition-colors">Reserve your seat →</button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials heading="Students who got licensed with us" />
      <FaqAccordion topic="course" />
    </div>
  );
}
